import * as React from "react";
import * as PropTypes from "prop-types";
import styled, { injectGlobal } from "styled-components";
import { BREAK_POINTS } from "@origin-digital/style-guide/dist/lib/consts/mediaQuery.const";
import { StyledLink } from "../../../Link";
import { Metrics, Palette, NavStructurePropTypes } from "../../vars";

import OverlayToolbar, { OverlayToolbarButton } from "../OverlayToolbar";
import OverlayHeading from "./OverlayHeading";
import OverlayContent from "./OverlayContent";
import OverlayAccordion from "./OverlayAccordion";

const { Heights, Timing } = Metrics;

const NO_SCROLL_CLASS = "no-scroll";

const BackLinkWrapper = styled.div`
  padding-top: ${Metrics.Padding.NAVBAR_ITEM_Y}px;
  padding-bottom: ${Metrics.Padding.NAVBAR_ITEM_Y}px;
`;
const BackLink = styled(StyledLink)`
  color: ${Palette.Rebrand.Colours.P6};
  padding-left: 18px;
  line-height: 24px;
`;

class OverlayNav extends React.Component<any, any> {
  rootRef: React.RefObject<HTMLDivElement>;

  static propTypes = {
    ...NavStructurePropTypes,
    open: PropTypes.bool,
    onCloseMenuClick: PropTypes.func.isRequired,
    onLogoutClick: PropTypes.func.isRequired
  };

  static defaultProps = {
    open: false
  };

  constructor(props) {
    super(props);

    this.state = {
      defaultNavKey: this.getNavItemKeyForCurrentPage(props.navStructure)
    };

    this.rootRef = React.createRef<HTMLDivElement>();
  }

  componentDidMount() {
    this.injectNoScrollClass();
    this.toggleNoScrollClass(this.props.open);
  }

  componentWillReceiveProps(nextProps) {
    this.toggleNoScrollClass(nextProps.open);
  }

  componentWillUnmount() {
    this.toggleNoScrollClass(false);
  }

  toggleNoScrollClass = open => {
    if (open) {
      document.body.classList.add(NO_SCROLL_CLASS);
    } else {
      document.body.classList.remove(NO_SCROLL_CLASS);
    }
  };

  injectNoScrollClass = () => {
    injectGlobal`
      body.${NO_SCROLL_CLASS} {
        overflow: hidden;
        position: fixed;
        width: 100%;
        height: 100%;

        @media (min-width: ${BREAK_POINTS.Min.LG}) {
          overflow: visible;
          position: static;
          width: auto;
          height: auto;
        }
      }
  `;
  };

  handleItemActivation = item => {
    setTimeout(() => {
      this.scrollToChild(item);
    }, Timing.OVERLAY_ACCORDION);
  };

  scrollToChild = child => {
    if (this.rootRef && this.rootRef.current && this.props.open) {
      const rect = child.getBoundingClientRect();
      const scrollTop =
        this.rootRef.current.scrollTop - Heights.OVERLAY_TOOLBAR + rect.top;

      this.rootRef.current.scrollTop = scrollTop;
    }
  };

  getNavItemKeyForCurrentPage = navStructure => {
    if (global.location) {
      // If this code is running in the browser.
      const pathname = global.location.pathname;

      return this.getNavItemKeyForPathname(navStructure, pathname);
    }
  };

  getNavItemKeyForPathname = (navStructure, pathname) => {
    const lastPathnameSegmentIndex = pathname.lastIndexOf("/");
    const pathnameParentPath = pathname.substr(0, lastPathnameSegmentIndex);

    return navStructure.reduce((acc, navItem, index) => {
      navItem.menuItems.forEach(({ href }) => {
        if (href) {
          const lastHrefSegmentIndex = href.lastIndexOf("/");
          const hrefParentPath = href.substr(0, lastHrefSegmentIndex);

          if (hrefParentPath == pathnameParentPath) {
            acc = index + 1;
          }
        }
      });

      return acc;
    }, null);
  };

  render() {
    const {
      className,
      navStructure,
      onCloseMenuClick,
      onLogoutClick,
      open
    } = this.props;
    const { defaultNavKey } = this.state;

    return (
      <div className={className} ref={this.rootRef}>
        <OverlayToolbar>
          <OverlayToolbarButton icon="close" onClick={onCloseMenuClick} />
          <OverlayToolbarButton
            icon="person_outline"
            onClick={onLogoutClick}
            className="log-out"
            pullRight
          >
            Log out
          </OverlayToolbarButton>
        </OverlayToolbar>
        <OverlayContent>
          <OverlayHeading>My Account</OverlayHeading>
          {open && (
            <OverlayAccordion
              defaultActiveKey={defaultNavKey}
              navStructure={navStructure}
              onItemActivation={this.handleItemActivation}
            />
          )}
          <BackLinkWrapper>
            <BackLink href="/">Back to Origin Homepage</BackLink>
          </BackLinkWrapper>
        </OverlayContent>
      </div>
    );
  }
}

export default styled(OverlayNav)`
  display: ${props => (props.open ? "block" : "none")};
  background-color: ${Palette.Rebrand.Colours.W1};
  width: 100%;
  height: 100%;
  position: fixed;
  overflow-y: scroll;
  top: 0;
  left: 0;
  z-index: 100;
`;
